// Função para traduzir e formatar mensagens de validação
import { getConsultantErrorMessage } from '../utils/consultantErrorMessages';

const translateValidationMessage = (message: any): string => {
  // Garantir que message seja sempre uma string
  if (message === null || message === undefined) {
    return '';
  }

  let translated = String(message);

  // Traduzir nomes de campos específicos (fazer antes das traduções gerais)
  translated = translated.replace(/\bbrandId\b/gi, 'Bandeira');
  translated = translated.replace(/\bcategoryId\b/gi, 'Categoria');
  translated = translated.replace(/\bpersonId\b/gi, 'Pessoa');
  translated = translated.replace(/\bmainUserId\b/gi, 'Usuário principal');
  translated = translated.replace(/\buserIds\b/gi, 'Usuários');
  translated = translated.replace(/\buserId\b/gi, 'Usuário');
  translated = translated.replace(/\bcompanyIds\b/gi, 'Postos autorizados');
  translated = translated.replace(/\boperationalBasesIds\b/gi, 'Bases operacionais');
  translated = translated.replace(/\brelationshipIds\b/gi, 'Relacionamentos');
  translated = translated.replace(/\banpCode\b/gi, 'Código ANP');
  translated = translated.replace(/\bsubscriptionPlan\b/gi, 'Plano de assinatura');
  translated = translated.replace(/\bactivatedAt\b/gi, 'Data de ativação');
  translated = translated.replace(/\bemail\b/gi, 'E-mail');
  translated = translated.replace(/\busername\b/gi, 'Nome de usuário');
  translated = translated.replace(/\bpassword\b/gi, 'Senha');
  translated = translated.replace(/\bname\b/gi, 'Nome');
  translated = translated.replace(/\btradeName\b/gi, 'Nome fantasia');
  translated = translated.replace(/\blegalName\b/gi, 'Razão social');
  translated = translated.replace(/\bpersonDocument\b/gi, 'Documento da pessoa');
  translated = translated.replace(/\bpersonType\b/gi, 'Tipo de pessoa');
  translated = translated.replace(/\breferenceDate\b/gi, 'Data de referência');
  translated = translated.replace(/\bsalesVolume\b/gi, 'Volume de vendas');
  translated = translated.replace(/\baverageRevenue\b/gi, 'Receita média');
  translated = translated.replace(/\bconvenienceRevenue\b/gi, 'Receita de conveniência');
  translated = translated.replace(/\boilChangeRevenue\b/gi, 'Receita de troca de óleo');
  translated = translated.replace(/\bfuelPumpRevenue\b/gi, 'Receita de bomba de combustível');

  // Traduzir mensagens de validação comuns
  translated = translated.replace(/\beach:\s*value in /gi, 'Cada valor em ');
  translated = translated.replace(/must be an email/gi, 'deve ser um e-mail válido');
  translated = translated.replace(/must be a UUID/gi, 'deve ser um UUID válido');
  translated = translated.replace(/must be an array/gi, 'deve ser uma lista');
  translated = translated.replace(/should not be empty/gi, 'não pode estar vazio');
  translated = translated.replace(/must be a string/gi, 'deve ser um texto');
  translated = translated.replace(/must be a number/gi, 'deve ser um número');
  translated = translated.replace(/must be longer than or equal to/gi, 'deve ter no mínimo');
  translated = translated.replace(/must be shorter than or equal to/gi, 'deve ter no máximo');
  translated = translated.replace(/is required/gi, 'é obrigatório');
  translated = translated.replace(/is not empty/gi, 'não pode estar vazio');
  translated = translated.replace(/must not be empty/gi, 'não pode estar vazio');
  translated = translated.replace(/cannot be empty/gi, 'não pode estar vazio');
  // Limpar e formatar a mensagem
  translated = translated.replace(/,([^\s])/g, ', $1'); // Adicionar espaço após vírgula se não houver
  translated = translated.replace(/\s+/g, ' ').trim();

  return translated;
};

// Função para formatar mensagens de erro de validação de forma mais clara
const formatValidationErrors = (message: any): string => {
  // Garantir que message seja sempre uma string
  if (message === null || message === undefined) {
    return 'Erro de validação desconhecido.';
  }

  const messageStr = String(message);

  // Se a mensagem contém múltiplos erros separados por vírgula
  if (messageStr.includes(',')) {
    // Dividir por vírgula, adicionar espaço se necessário e processar cada erro
    // Primeiro, normalizar espaços após vírgulas (incluindo casos sem espaço)
    let normalizedMessage = messageStr.replace(/,([^\s])/g, ', $1');
    // Também tratar casos onde há múltiplas vírgulas seguidas
    normalizedMessage = normalizedMessage.replace(/,\s*,/g, ', ');
    const errors = normalizedMessage
      .split(',')
      .map((err) => err.trim())
      .filter((err) => err.length > 0);

    // Agrupar erros por campo
    const errorsByField: { [key: string]: string[] } = {};

    errors.forEach((error) => {
      // Padrões comuns: "campo must be X", "campo should not be empty", etc.
      let field = '';
      let errorMessage = '';

      // Tentar extrair campo e mensagem de diferentes formatos
      // Formato: "email must be an email" ou "username should not be empty"
      const fieldMatch = error.match(/^(\w+)\s+(.+)$/i);

      if (fieldMatch) {
        field = fieldMatch[1];
        errorMessage = fieldMatch[2];
      } else {
        // Se não encontrou padrão, tentar pegar a primeira palavra como campo
        const words = error.split(/\s+/);
        field = words[0] || 'Campo';
        errorMessage = words.slice(1).join(' ');
      }

      // Traduzir o campo
      const translatedField = translateValidationMessage(field);
      // Traduzir a mensagem de erro
      const translatedMessage = translateValidationMessage(errorMessage);

      if (!errorsByField[translatedField]) {
        errorsByField[translatedField] = [];
      }

      // Adicionar a mensagem traduzida
      errorsByField[translatedField].push(translatedMessage);
    });

    // Formatar mensagem final de forma mais clara e legível
    // Criar mensagens separadas para cada erro individual
    const formattedErrors: string[] = [];

    Object.entries(errorsByField).forEach(([field, fieldErrors]) => {
      const uniqueErrors = [...new Set(fieldErrors)];
      uniqueErrors.forEach((error) => {
        // Criar uma mensagem separada para cada erro
        // Formato: "Campo: erro" ou "Campo erro" dependendo do contexto
        if (error.startsWith('deve') || error.startsWith('é') || error.startsWith('não')) {
          formattedErrors.push(`${field} ${error}`);
        } else {
          formattedErrors.push(`${field}: ${error}`);
        }
      });
    });

    // Retornar mensagens separadas, uma por linha
    return formattedErrors.map((err) => `• ${err}`).join('\n');
  }

  // Se for uma única mensagem, apenas traduzir
  return translateValidationMessage(messageStr);
};

export const handleApiError = (error: any) => {
  // Idempotente: serviços já lançam `throw handleApiError(axiosErr)`.
  // Se chamar de novo num Error sem response Axios, preserva a mensagem.
  if (
    error instanceof Error &&
    !(error as { response?: unknown }).response &&
    !(error as { isAxiosError?: boolean }).isAxiosError
  ) {
    return error;
  }

  // Verificar se há detalhes de validação no erro
  if (error.response?.data?.errors && Object.keys(error.response.data.errors).length > 0) {
    // Criar uma mensagem com os detalhes de validação
    const errorFields = Object.keys(error.response.data.errors)
      .map((field) => {
        const fieldErrors = error.response.data.errors[field];
        const translatedField = translateValidationMessage(field);

        // Garantir que fieldErrors seja um array
        const errorsArray = Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors];
        const translatedErrors = errorsArray.map((err: any) => translateValidationMessage(err));

        return `${translatedField}: ${translatedErrors.join(', ')}`;
      })
      .join('; ');

    return new Error(`Erro de validação: ${errorFields}`);
  }

  // Tentar capturar a mensagem específica do backend primeiro
  const backendMessage = error.response?.data?.message;

  if (error.response?.status === 400) {
    if (backendMessage) {
      // Formatar e traduzir a mensagem de validação
      // Garantir que backendMessage seja uma string
      const formattedMessage = formatValidationErrors(String(backendMessage));
      return new Error(formattedMessage);
    }
    return new Error('Erro de validação: os dados fornecidos estão incorretos.');
  }

  if (error.response?.status === 409) {
    if (backendMessage) {
      const text = Array.isArray(backendMessage)
        ? backendMessage.map(String).join(' ')
        : String(backendMessage);
      return new Error(text);
    }
    return new Error('Conflito: já existe um registro com estes dados.');
  }

  if (error.response?.status === 401) {
    if (backendMessage) {
      return new Error(`Não autorizado: ${backendMessage}`);
    }
    return new Error('Não autorizado: você não tem permissão para realizar essa ação.');
  }

  if (error.response?.status === 403) {
    const errorCode = error.response?.data?.code;
    const consultantMessage = getConsultantErrorMessage(errorCode);
    if (consultantMessage) {
      return new Error(consultantMessage);
    }
    if (backendMessage) {
      return new Error(`Acesso negado: ${backendMessage}`);
    }
    return new Error('Acesso negado: você não tem permissão para realizar essa ação.');
  }

  if (error.response?.status === 429) {
    const retryAfterSec = error.response?.data?.retryAfterSec;
    if (typeof retryAfterSec === 'number' && retryAfterSec > 0) {
      return new Error(`Muitas tentativas. Aguarde ${retryAfterSec} segundos e tente novamente.`);
    }
    if (backendMessage) {
      return new Error(backendMessage);
    }
    return new Error('Muitas tentativas. Aguarde um momento e tente novamente.');
  }

  if (error.response?.status === 404) {
    if (backendMessage) {
      return new Error(`Não encontrado: ${backendMessage}`);
    }
    return new Error('Recurso não encontrado: o endpoint solicitado não existe.');
  }

  if (error.response?.status === 500) {
    if (backendMessage) {
      return new Error(String(backendMessage));
    }
    return new Error('Erro interno do servidor: algo deu errado, tente novamente mais tarde.');
  }

  if (error.response?.status === 503) {
    return new Error('Serviço indisponível: o servidor está temporariamente fora do ar.');
  }

  if (error.response?.status && error.response.status >= 500) {
    return new Error('Erro interno do servidor: algo deu errado, tente novamente mais tarde.');
  }

  // Se há uma mensagem específica do backend, usar ela mesmo sem status específico
  if (backendMessage) {
    return new Error(backendMessage);
  }

  // Erro genérico para qualquer outro tipo de falha
  return new Error('Erro desconhecido. Tente novamente mais tarde.');
};
